import { Store } from 'stores/type';
import { SFX, MUSIC, FADE_DURATION } from 'constants/audio';
import { SoundType } from 'types';
import { delay } from 'utils';

const MIN_SFX = 0;
const MAX_SFX = 0.6;
const MAX_MUSIC = 0.08;

const FADE_OUT_GAIN = 0.01;

class VolumeManager {
  private $store: Store;
  private context: AudioContext;
  public sfx: GainNode;
  public music: GainNode;
  public gain: GainNode;

  constructor(context: AudioContext, store: Store) {
    this.$store = store;
    this.context = context;

    this.sfx = this.context.createGain();
    this.setVolume(this.sfx.gain, MAX_SFX);

    this.music = this.context.createGain();
    this.setVolume(this.music.gain, FADE_OUT_GAIN);

    this.gain = this.context.createGain();

    this.sfx.connect(this.gain);
    this.music.connect(this.gain);
    this.gain.connect(this.context.destination);

    this.initWatchers();
  }
  private initWatchers() {
    this.$store.watch(
      (_state, getters) => getters.playMusic,
      (canPlay) => this[canPlay ? 'unmute' : 'mute'](MUSIC),
    );

    this.$store.watch(
      (_state, getters) => getters.playSfx,
      (canPlay) => this[canPlay ? 'unmute' : 'mute'](SFX),
    );
  }

  public mute(type: SoundType) {
    const MIN = type === MUSIC ? FADE_OUT_GAIN : MIN_SFX;
    this.setVolumeSmooth(this[type].gain, MIN);
  }
  public unmute(type: SoundType, musicMax = MAX_MUSIC) {
    const MAX = type === MUSIC ? this.getMusicMax(musicMax) : MAX_SFX;
    this.setVolumeSmooth(this[type].gain, MAX);
  }

  public async fadeInMusic(duration = FADE_DURATION, musicMax = MAX_MUSIC) {
    if (this.canPlay(MUSIC)) {
      const volume = this.getMusicMax(musicMax);
      if (this.music.gain.value !== volume) {
        this.setVolume(this.music.gain, FADE_OUT_GAIN);
      }

      this.music.gain.exponentialRampToValueAtTime(volume, this.context.currentTime + duration);
    }

    await delay(duration * 1000);
  }
  public async fadeOutMusic(duration = FADE_DURATION) {
    this.music.gain.exponentialRampToValueAtTime(FADE_OUT_GAIN, this.context.currentTime + duration);

    await delay(duration * 1000);
  }

  public canPlay(type: SoundType) {
    return type === MUSIC ? this.$store.getters.playMusic : this.$store.getters.playSfx;
  }

  private setVolume(gain: AudioParam, value: number) {
    gain.setValueAtTime(value, this.context.currentTime);
  }
  private setVolumeSmooth(gain: AudioParam, value: number) {
    const SMOOTHING_DURATION = 0.25;
    gain.setTargetAtTime(value, this.context.currentTime, SMOOTHING_DURATION);
  }

  private getMusicMax(max: number) {
    if (max === 100) {
      return MAX_MUSIC;
    }

    return (max / 100) * MAX_MUSIC;
  }
}

export default VolumeManager;
